import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Timeline,
} from 'antd';
import { useRequest } from 'ahooks';
import {
  systemController,
  userController,
  BaseUserDTO,
  UserStateEnum,
  SendCommandRequest,
} from '../../api';
import { useState } from 'react';
import { CopyOutlined } from '@ant-design/icons';
import { BaseApi, apiHost } from '../../apis/request';

const { TextArea } = Input;

interface ShellLogItem {
  id: number;
  createdAt: string;
  remark: string;
}

interface ParsedShellLog {
  id: number;
  createdAt: string;
  userAgent: string;
  exitCode: number;
  log: string;
  deviceId: string;
  createTime: number;
  endTime: number;
}

function parseShellLog(item: ShellLogItem): ParsedShellLog | null {
  try {
    const content = item.remark.replace(/^shell_log:\n/, '');
    const firstNewline = content.indexOf('\n');
    const userAgent = content.substring(0, firstNewline);
    const jsonStr = content.substring(firstNewline + 1);
    const data = JSON.parse(jsonStr);
    return {
      id: item.id,
      createdAt: item.createdAt,
      userAgent,
      exitCode: data.exitCode,
      log: data.log,
      deviceId: data.deviceId,
      createTime: data.createTime,
      endTime: data.endTime,
    };
  } catch {
    return null;
  }
}

function formatTimestamp(ts: number) {
  return new Date(ts).toLocaleString('zh-CN');
}

const SendCommand = () => {
  const [form] = Form.useForm<{ companyId: number; userId: number; cmd: string }>();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>();
  const [logsQueried, setLogsQueried] = useState(false);

  const { data: companies, loading: companiesLoading } = useRequest(() => {
    return systemController.listCompany();
  });

  const { data: users, loading: usersLoading } = useRequest(
    () => {
      return userController
        .listAllBaseUser(selectedCompanyId!)
        .then((list) =>
          list.filter(({ state }) => state !== UserStateEnum.DELETED),
        );
    },
    {
      ready: !!selectedCompanyId,
      refreshDeps: [selectedCompanyId],
    },
  );

  const { runAsync: submit, loading: submitting } = useRequest(
    (request: SendCommandRequest) => {
      return systemController.sendCommand(request);
    },
    { manual: true },
  );

  const {
    data: shellLogs,
    loading: logsLoading,
    runAsync: fetchShellLogs,
  } = useRequest(
    (userId: number) => {
      return BaseApi.request<ShellLogItem[]>({
        method: 'GET',
        url: `${apiHost}/system/shell_log`,
        params: { userId },
      });
    },
    { manual: true },
  );

  const onFinish = async (values: {
    companyId: number;
    userId: number;
    cmd: string;
  }) => {
    try {
      await submit({ userId: values.userId, cmd: values.cmd });
      message.success('命令已下发');
    } catch {
      message.error('下发命令失败，请重试');
    }
  };

  const handleQueryLogs = async () => {
    const userId = form.getFieldValue('userId');
    if (!userId) {
      message.warning('请先选择用户');
      return;
    }
    setLogsQueried(true);
    try {
      await fetchShellLogs(userId);
    } catch {
      message.error('查询日志失败');
    }
  };

  const parsedLogs = (shellLogs ?? [])
    .slice(0, 10)
    .map(parseShellLog)
    .filter((item): item is ParsedShellLog => item !== null);

  return (
    <Row gutter={16}>
      <Col span={logsQueried ? 10 : 24}>
        <Card title="下发命令">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ maxWidth: 600 }}
          >
            <Form.Item
              name="companyId"
              label="选择公司"
              rules={[{ required: true, message: '请选择公司' }]}
            >
              <Select
                placeholder="请选择公司"
                loading={companiesLoading}
                notFoundContent={companiesLoading ? <Spin size="small" /> : undefined}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.includes(input)
                }
                options={companies?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                onChange={(value) => {
                  setSelectedCompanyId(value);
                  form.setFieldValue('userId', undefined);
                }}
              />
            </Form.Item>
            <Form.Item
              name="userId"
              label="选择用户"
              rules={[{ required: true, message: '请选择用户' }]}
            >
              <Select
                placeholder={selectedCompanyId ? '请选择用户' : '请先选择公司'}
                disabled={!selectedCompanyId}
                loading={usersLoading}
                showSearch
                filterOption={(input, option) => {
                  const user = (option as unknown as { user: BaseUserDTO })?.user;
                  return !!(
                    user?.name?.includes(input) ||
                    String(user?.id).includes(input) ||
                    user?.mobile?.includes(input)
                  );
                }}
                options={users?.map((user) => ({
                  label: (
                    <Space>
                      <span>{user.name}</span>
                      <span>({user.id})</span>
                    </Space>
                  ),
                  value: user.id,
                  user,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="cmd"
              label="执行命令"
              rules={[{ required: true, message: '请输入命令' }]}
            >
              <TextArea rows={4} placeholder="请输入要执行的命令" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  下发命令
                </Button>
                <Button onClick={handleQueryLogs} loading={logsLoading}>
                  查询日志
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
      {logsQueried && (
        <Col span={14}>
          <Card title="Shell 日志">
            {logsLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : parsedLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
                暂无日志
              </div>
            ) : (
              <Timeline
                items={parsedLogs.map((log) => ({
                  children: (
                    <div key={log.id}>
                      <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="记录时间" span={2}>
                          {log.createdAt}
                        </Descriptions.Item>
                        <Descriptions.Item label="退出码">
                          <Tag color={log.exitCode === 0 ? 'green' : 'red'}>
                            {log.exitCode}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="设备ID">
                          {log.deviceId}
                        </Descriptions.Item>
                        <Descriptions.Item label="执行时间" span={2}>
                          {formatTimestamp(log.createTime)} ~{' '}
                          {formatTimestamp(log.endTime)}
                          （{log.endTime - log.createTime}ms）
                        </Descriptions.Item>
                        <Descriptions.Item label="UA" span={2}>
                          <span style={{ fontSize: 12, wordBreak: 'break-all' }}>
                            {log.userAgent}
                          </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="输出" span={2}>
                          <div style={{ position: 'relative' }}>
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                zIndex: 1,
                              }}
                              onClick={() => {
                                navigator.clipboard.writeText(log.log).then(
                                  () => message.success('已复制'),
                                  () => message.error('复制失败'),
                                );
                              }}
                            />
                            <pre
                              style={{
                                maxHeight: 200,
                                overflow: 'auto',
                                fontSize: 12,
                                margin: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {log.log}
                            </pre>
                          </div>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  ),
                }))}
              />
            )}
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default SendCommand;
